import React, { useState } from 'react';
import Input from '../components/Input';

export default function SenderCard({
  sender,
  index,
  connectorType,
  isPrimary,
  onSetPrimary,
  routingStrategy,
  onUpdate,
  onRemove,
  errors,
}) {
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await onRemove(index);
    } finally {
      setRemoving(false);
    }
  };

  // ROUND_ROBIN  = per-type primary selection via radio button
  // ROUND_ROBIN_BY_NUMBER = even split across active senders (active checkbox drives it)
  const isRoundRobin         = routingStrategy === 'ROUND_ROBIN';
  const isAnyRoundRobin      = isRoundRobin || routingStrategy === 'ROUND_ROBIN_BY_NUMBER';

  return (
    <div className="rounded-lg bg-gray-50 shadow-sm outline outline-1 outline-gray-900/5 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10">
      {/* Card header */}
      <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">

            {/* Radio button — only shown for ROUND_ROBIN (per-type primary selection) */}
            {isRoundRobin && (
              <button
                type="button"
                onClick={onSetPrimary}
                title={isPrimary
                  ? 'Primary sender for this connector type'
                  : 'Set as primary sender for this connector type'}
                className="flex-shrink-0 focus:outline-none"
                aria-label={isPrimary ? 'Primary' : 'Set as primary'}
              >
                <span
                  className={[
                    'inline-flex items-center justify-center w-5 h-5 rounded-full border-2 transition-colors',
                    isPrimary
                      ? 'border-indigo-600 bg-indigo-600'
                      : 'border-gray-400 bg-white dark:bg-gray-700 hover:border-indigo-400',
                  ].join(' ')}
                >
                  {isPrimary && <span className="w-2 h-2 rounded-full bg-white" />}
                </span>
              </button>
            )}

            <div>
              <p className="text-md font-light text-black dark:text-white">
                Sender {index + 1}:{' '}
                <span className="font-medium">{sender.name}</span>
                {connectorType && (
                  <span className="ml-2 inline-flex items-center rounded-md bg-indigo-50 dark:bg-indigo-900/40 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 ring-1 ring-inset ring-indigo-700/10 dark:ring-indigo-400/30">
                    {connectorType}
                  </span>
                )}
              </p>
              {isRoundRobin && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {isPrimary
                    ? "Primary — receives this connector type's traffic share"
                    : 'Non-primary — receives 0% (click ● to make primary)'}
                </p>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleRemove}
            disabled={removing}
            className="text-md font-medium text-red-500 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {removing ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </div>

      {/* Card body */}
      <div className="space-y-12 p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-8">
          <div className="col-span-2 sm:col-span-1">
            <Input
              label="Sender name"
              required
              type="text"
              disabled
              value={sender.name}
              onChange={(e) => onUpdate(index, 'name', e.target.value)}
              error={errors?.[index]?.name?.message}
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Input
              label="Phone number"
              required
              type="text"
              disabled
              value={sender.number}
              onChange={(e) => onUpdate(index, 'number', e.target.value)}
              error={errors?.[index]?.number?.message}
            />
          </div>

          {/* Percentage — auto-calculated for both round-robin strategies */}
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Percentage ({Math.round((Number(sender.percentage) || 0) * 100)}%)
              {isAnyRoundRobin && (
                <span className="ml-1 text-xs font-normal text-gray-400 dark:text-gray-500">
                  — auto-calculated
                </span>
              )}
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={sender.percentage}
              onChange={(e) => onUpdate(index, 'percentage', Number(e.target.value))}
              disabled={isAnyRoundRobin}
              className={[
                'w-full h-2 rounded-lg appearance-none mt-5',
                isAnyRoundRobin
                  ? 'bg-gray-200 dark:bg-gray-700 opacity-50 cursor-not-allowed'
                  : 'bg-gray-200 dark:bg-gray-700 cursor-pointer',
              ].join(' ')}
            />
          </div>

          <div className="col-span-2 sm:col-span-1">
            <Input
              label="Rate"
              required
              type="number"
              value={sender.rate}
              onChange={(e) => onUpdate(index, 'rate', Number(e.target.value))}
              error={errors?.[index]?.rate?.message}
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Input
              label="Active"
              label_description="Keep this sender active"
              as="checkbox"
              checked={!!sender.active}
              onChange={(e) => onUpdate(index, 'active', e.target.checked)}
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Input
              label="Default sender"
              label_description="Make this the default sender"
              as="checkbox"
              checked={!!sender.defaultNumber}
              onChange={(e) => onUpdate(index, 'defaultNumber', e.target.checked)}
              error={errors?.[index]?.defaultNumber?.message}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
